import {
  Autocomplete,
  Body,
  Button,
  ButtonBar,
  Container,
  Header,
  OutlinedButton,
  Popper,
  Row,
  SvgIcon,
  UserRow
} from './styled';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce, get, uniq } from 'lodash';
import { removeConnections, useQueryRefresh, useUpdateUser } from 'shared/apollo';

import { EditUser } from './EditUser';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';

import { SearchInputContainer } from 'shared/components/SearchInput';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import VirtualizedView from 'shared/components/VirtualizedView';
import YesNoDialogButton from 'shared/components/YesNoDialogButton';
import { columnTypes } from 'shared/components/tables';
import { env } from 'shared/apollo/client';
import { gql } from 'apollo-boost';
import { useLambda } from 'shared/services/lambda';
import { useUser } from 'shared/auth';
import { ConfirmationDialog } from 'shared/components';
import { NewUser } from './NewUser';

const roles = {
  admin: 'Admin',
  poweruser: 'Power User',
  basic: 'Basic',
  guest: 'Guest',
};

const getRole = role => roles[role] || role;

export default function ManagerUsers() {
  const user = useUser();
  const handleDisableUserDone = useRef();
  const closeDisableUserDialog = useRef();
  /*  const {
      result: users,
      error,
      load: listUsers,
    } = useLambda('listUsers')*/
  const { data: usersData, refresh: listUsers, isLoading } = useQueryRefresh(usersQuery);
  const { run: runUpdate } = useUpdateUser();
  //  const { run: runDisableUser } = useDisableUser();
  const users = usersData && removeConnections(usersData.allUsers);
  const error = null;
  const searchOptions = useMemo(() => users && uniq(
    quickFilterFields
      .reduce((all, field) => [
        ...all,
        ...users.map(row => get(row, field))
      ], [])
  )
    .filter(option => option)
    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
    , [users]);

  const {
    load: disableUser
  } = useLambda('users/disableUser', null, handleDisableUserDone);
  const handleListUsers = useCallback(() => {
    if (!user || !user.effectiveUser || !listUsers) {
      return;
    }
    if (closeDisableUserDialog.current) {
      closeDisableUserDialog.current();
      closeDisableUserDialog.current = null;
    }
    listUsers({
      env,
      accountForUsername: user.effectiveUser.username,
    });
  }, [listUsers, user]);
  useEffect(() => {
    handleListUsers();
    handleDisableUserDone.current = handleListUsers;
  }, [handleListUsers]);

  // TODO: This is not a good solution,
  // We will have to create smaller components.
  let isDirty = false;
  const virtualized = useRef();
  const [filter, setFilter] = useState();
  const [dialogDismiss, setDialogDismiss] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const onDirty = useCallback((dirty) => { isDirty = dirty; }, [isDirty]);

  const handleSearchFocus = useCallback(() => {
    setIsSearchFocused(true);
  }, []);
  const handleSearchBlur = useCallback(() => {
    setIsSearchFocused(false);
  }, []);
  const debouncedSetFilter = useMemo(() => debounce(setFilter, 500), []);
  const handleSearchInput = useCallback((event, value) => {
    debouncedSetFilter(value);
  }, [debouncedSetFilter]);
  const handleSearchRenderInput = useCallback(params => (
    <SearchInputContainer maxWidth={400}>
      <TextField
        placeholder={isSearchFocused ? undefined : 'Search users'}
        onFocus={handleSearchFocus}
        onBlur={handleSearchBlur}
        {...params}
      />
    </SearchInputContainer>
  ), [handleSearchBlur, handleSearchFocus, isSearchFocused]);

  const [isNewUserDrawerOpen, setIsNewUserDrawerOpen] = useState(false);
  const accountId = user?.attributes && user?.attributes['custom:account_id'];
  const handleAdd = useCallback(() => {
    setIsNewUserDrawerOpen(true);
  }, []);
  const handleNewUserDrawerClose = useCallback(() => {
    if (isDirty) {
      setDialogDismiss(true);
      return;
    }
    setIsNewUserDrawerOpen(false);
  }, [isDirty, setIsNewUserDrawerOpen, setDialogDismiss]);

  const [isUserDrawerOpen, setIsUserDrawerOpen] = useState(false);
  const [usernameForEdit, setSsernameForEdit] = useState();
  const [selectedUser, setSelectedUser] = useState();
  const handleEdit = useCallback((props) => {
    setIsUserDrawerOpen(true);
    setSsernameForEdit(props.email);
    setSelectedUser(props);
    //    push(`/account/edit-user/${username}`)
  }, []);
  const handleEditDrawerClose = useCallback(() => {
    if (isDirty) {
      setDialogDismiss(true);
    } else {
      setIsUserDrawerOpen(false);
      setSsernameForEdit(undefined);
      setSelectedUser(undefined);
    }
  }, []);
  const handleCancelDismiss = useCallback(() => {
    setDialogDismiss(false);
  }, [setDialogDismiss]);
  const handleConfirmDismiss = useCallback(() => {
    isDirty = false;
    setDialogDismiss(false);
    handleNewUserDrawerClose();
    handleNewUserDrawerClose();
  }, [isDirty, setDialogDismiss, handleNewUserDrawerClose, handleNewUserDrawerClose]);
  const isAdmin = user?.attributes && user?.attributes['custom:type'] === 'admin';
  const columns = useMemo(() => [
    {
      headerName: 'Name',
      valueGetter: ({ data }) => `${data.firstName} ${data.lastName}`,
      cellRenderer: ({ value }) => <b>{value}</b>,
      flex: 1,
      sortable: true,
    },
    { headerName: 'Email', field: 'email', width: 300, sortable: true },
    { headerName: 'Job title', field: 'title', width: 200, sortable: true },
    { headerName: 'Role', valueGetter: ({ data }) => getRole(data.userType), width: 100, sortable: true },
    isAdmin
      ? {
        headerName: '',
        width: 100,
        cellRenderer: ({ data: { userId, email, firstName, lastName, pronoun, userType, phone, title } }) => (
          <Row className="show-on-hover">
            <Tooltip arrow title="Edit">
              <IconButton onClick={() => handleEdit({ userId, email, firstName, lastName, pronoun, userType, phone, title })} size="small">
                <SvgIcon name="Edit" />
              </IconButton>
            </Tooltip>
            &nbsp;
            <YesNoDialogButton
              buttonComponent={IconButton}
              buttonContent={<Tooltip arrow title="Delete"><SvgIcon name="Delete" /></Tooltip>}
              dialogTitle="Delete a User"
              positiveLabel="Delete User"
              negativeLabel="Cancel"
              onSubmit={async (close) => {
                await disableUser({ env, username: email });
                runUpdate({ id: userId, enabled: 0 }, async () => {
                  await listUsers();
                  close();
                });
                //closeDisableUserDialog.current = close
                return false;
              }}
            >
              <div>Please confirm you wish to permanently delete the following user. This action is permanent:</div>
              <div>{firstName} {lastName} ({email})</div>
            </YesNoDialogButton>
          </Row>
        )
      }
      : undefined
  ], [disableUser, handleEdit, isAdmin, listUsers, runUpdate]);
  const groups = useMemo(() => users
    ?.filter(obj => !filter || matchQuickFilter(obj, filter)),
    [users, filter]);
  const handleRenderRow = useCallback(({
    value,
    columns,
    style, // Style object to be applied to row (to position it)
    selectedRows,
    setSelectedRows,
    hasCheckBox,
    isLastRow,
  }) => {
    return (
      <UserRow
        {...{
          style, value, columns, columnTypes,
          selectedRows, setSelectedRows, hasCheckBox,
          isLastRow,
        }}
        keyField="email"
      />
    );
  }, []);
  const [selection, setSelection] = useState([]);
  const handleGetRowHeight = useCallback(() => 64 + 5, []);

  if (!user) {
    return <LinearProgress variant="query" />;
  }

  return (<Container>
    <Header>
      <h2>Manage Users<b>({users?.length ?? '...'})</b></h2>
      <Row>
        <Autocomplete
          id="users-search"
          freeSolo
          options={searchOptions}
          renderInput={handleSearchRenderInput}
          onInputChange={handleSearchInput}
          PopperComponent={Popper}
        />
        {isAdmin && <Button
          color="primary"
          aria-label="add"
          onClick={handleAdd}
        >
          Add new user
        </Button>}
      </Row>
    </Header>
    <Body style={{ paddingLeft: '30px' }}>
      {error}
      <EditUser
        isUserDrawerOpen={isUserDrawerOpen}
        handleEditDrawerClose={handleEditDrawerClose}
        accountId={accountId}
        usernameForEdit={usernameForEdit}
        selectedUser={selectedUser}
        setSelectedUser={setSelectedUser}
        listUsers={listUsers}
        onDirty={onDirty}
      />
      <NewUser
        isNewUserDrawerOpen={isNewUserDrawerOpen}
        handleNewUserDrawerClose={handleNewUserDrawerClose}
        accountId={accountId}
        listUsers={listUsers}
        onDirty={onDirty}
      />
      <ButtonBar>
        {selection?.length > 0
          ? <YesNoDialogButton
            buttonComponent={OutlinedButton}
            buttonContent="Delete"
            positiveLabel={selection.length === 1 ? 'Delete a User' : 'Delete Users'}
            negativeLabel="Cancel"
            dialogTitle={selection.length === 1 ? 'Delete a User' : 'Delete Users'}
            onSubmit={async (close) => {
              for (const { email } of selection) {
                await disableUser({ env, username: email });
                runUpdate({ id: userId, enabled: 0 }, async () => { //eslint-disable-line
                  await listUsers();
                });
              }
              close();
              //closeDisableUserDialog.current = close
              return false;
            }}
          >
            <div>Please confirm you wish to permanently delete the following user{selection.length === 1 ? '' : 's'}. This action is
            permanent:</div>
            <ol>
              {selection.map(({ firstName, lastName, email }) => (
                <li key={email}>{firstName} {lastName} ({email})</li>
              ))}
            </ol>
          </YesNoDialogButton>
          : null}
      </ButtonBar>
      <ConfirmationDialog
        openDialog={dialogDismiss}
        onCloseDialog={handleCancelDismiss}
        actionButtonOne={handleConfirmDismiss}
        actionButtonTwo={handleCancelDismiss}
        textButtonOne="Confirm"
        textButtonTwo="Cancel"
        title="You have made some changes to this user, any unsaved changes will be lost."
      />
      <VirtualizedView
        ref={virtualized}
        view="grid"
        defaultSortByColumn="Name"
        {...{ groups, columns, isLoading }}
        onRenderRow={handleRenderRow}
        onSelectionChanged={setSelection}
        onGetRowHeight={handleGetRowHeight}
        tableExtraPadding={60}
      //        onExpansionChanged={handleExpansion}
      //        showMoreFilters={showMoreFilters}
      />
      {/*      <TableContainer>
        <Table>
          <TableHead>
            <TableCell>Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Job Title</TableCell>
            <TableCell>Role</TableCell>
            {isAdmin && <TableCell></TableCell>}
          </TableHead>
          <TableBody>
          {users && users
            .filter(({ enabled }) => enabled)
            .map(({ userId, firstName, lastName, email, title, userType }) => (
              <TableRow key={userId}>
                <ColumnHeader><Checkbox color="primary"/>{firstName} {lastName}</ColumnHeader>
                <ColumnHeader>{email}</ColumnHeader>
                <ColumnHeader>{title}</ColumnHeader>
                <ColumnHeader>{getRole(userType)}</ColumnHeader>
                {isAdmin && <ColumnHeader>
                </ColumnHeader>}
              </TableRow>
            ))
          }
          </TableBody>
        </Table>
        </TableContainer>*/}
    </Body>
  </Container>);
}


const usersQuery = gql`query {
  allUsers(filters: {enabled: true}) {
    edges {
      node {
        userId
        firstName
        lastName
        email
        title
        phone
        userType
        enabled
        pronoun
      }
    }
  }
}`;
/*
const disableUserMutation = gql` mutation disableUser($userId: Int!) {
  updateUser(id: $userId, enabled: false) {
    success
  }
}`
*/
const quickFilterFields = [
  'firstName',
  'lastName',
  'email',
];

const matchQuickFilter = (obj, filter) => !filter
  || quickFilterFields
    .some(field => String(get(obj, field)).toLowerCase().includes(filter.toLowerCase()));
/*
function useDisableUser() {
  const { data, isLoading, mutate } = useMutation()
  const run = (variables, onFinish) => {
    mutate(disableUserMutation, variables, () => {
      if (typeof onFinish === "function") {
        onFinish()
      }
    })
  }
  return { data, isLoading, run }
}
*/